import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

export const parseURLFilterValue = <T>(serializedValue: string | null, defaultValue: T): T => {
  if (!serializedValue) {
    return defaultValue;
  }

  try {
    return JSON.parse(serializedValue) as T;
  } catch {
    return defaultValue;
  }
};

export const useURLFilter = <T = Record<string, unknown>>(
  key: string,
  defaultValue: T
): [T, (newValue: T) => void] => {
  const [params, setParams] = useSearchParams();

  const value = useMemo(() => {
    return parseURLFilterValue(params.get(key), defaultValue);
  }, [defaultValue, key, params]);

  const setValue = useCallback(
    (newValue: T) =>
      setParams((prev) => ({...Object.fromEntries(prev), [key]: JSON.stringify(newValue)})),
    [key, setParams]
  );

  return [value, setValue];
};
